import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "oidc-react";
import { useApiClient } from "@/hooks/useApiClient";
import { isPublicRoute } from "@/constants";
import { realmFromIssuer } from "@/config/realm";
import FullScreenSpinner from "./FullScreenSpinner";

interface RealmGuardProps {
  children: React.ReactNode;
}

/**
 * Enforces realm-based access control and sets API authentication token:
 * - B2B users can only access /b2b/* routes
 * - Lantbrukare users can only access non-/b2b routes
 * - The user's realm is read from the token issuer, compared against the
 *   configured realm authorities (see config/realm.ts)
 * - Injects OIDC token into API client before rendering children
 * - Public routes (like /share/:token) skip all authentication checks
 */
const RealmGuard: React.FC<RealmGuardProps> = ({ children }) => {
  const location = useLocation();

  // Skip authentication for public routes
  if (isPublicRoute(location.pathname)) {
    return <>{children}</>;
  }

  const { userData, isLoading } = useAuth();
  const navigate = useNavigate();

  // Inject OIDC token into API client as soon as authentication completes
  useApiClient();

  const isB2bRoute = location.pathname.startsWith("/b2b");

  useEffect(() => {
    if (!isLoading && userData) {
      const realm = realmFromIssuer(userData.profile?.iss);

      // Redirect if user is in wrong realm
      if (realm === "b2b" && !isB2bRoute) {
        console.debug(
          "B2B user accessing Lantbrukare route, redirecting to /b2b"
        );
        navigate("/b2b", { replace: true });
      } else if (realm === "lantbrukare" && isB2bRoute) {
        console.debug(
          "Lantbrukare user accessing B2B route, redirecting to /dashboard"
        );
        navigate("/dashboard", { replace: true });
      } else if (realm === null) {
        // The library only accepts a token whose issuer equals the configured
        // authority, so this means appConfig.js disagrees with the session.
        // Let the user through rather than lock everyone out of a misconfigured
        // environment; the warning makes the misconfiguration visible.
        console.warn(
          "[RealmGuard] Token issuer matches neither configured realm authority; realm enforcement skipped",
          userData.profile?.iss
        );
      }
    }
  }, [userData, isLoading, isB2bRoute, navigate, location.pathname]);

  if (isLoading || !userData) {
    return <FullScreenSpinner />;
  }

  return <>{children}</>;
};

export default RealmGuard;
